interface MaxLimitParams {
  containerSize: number;
  elementSize: number;
  scale: number;
}

interface ConstrainedPanParams {
  containerWidth: number;
  containerHeight: number;
  contentWidth: number;
  contentHeight: number;
  scale: number;
  currentX: number;
  currentY: number;
  deltaX: number;
  deltaY: number;
}

interface ContentFocalPointParams {
  rawFocalX: number;
  rawFocalY: number;
  containerWidth: number;
  containerHeight: number;
  contentWidth: number;
  contentHeight: number;
  scale: number;
  positionX: number;
  positionY: number;
}

const PERCENTAGE_SCALE = 100;
const EDGE_BUFFER_PX = 1;
const UNSCALED_RATIO = 1;
const CENTER_DIVISOR = 2;
const CONTENT_CENTER = 0.5;

export const DEFAULT_DEVICE_PIXEL_RATIO = 1;
export const MAX_DEVICE_PIXEL_RATIO = 3;

export const normaliseDevicePixelRatio = (devicePixelRatio: number): number =>
  Number.isFinite(devicePixelRatio) && devicePixelRatio > 0
    ? Math.min(devicePixelRatio, MAX_DEVICE_PIXEL_RATIO)
    : DEFAULT_DEVICE_PIXEL_RATIO;

export const calculateMinLimit = ({
  elementSize,
  scale,
}: {
  elementSize: number;
  scale: number;
}): number => {
  const bufferPercent =
    (EDGE_BUFFER_PX / (elementSize * scale)) * PERCENTAGE_SCALE;
  return (
    ((UNSCALED_RATIO - scale) / (CENTER_DIVISOR * scale)) * PERCENTAGE_SCALE +
    bufferPercent
  );
};

export const calculateMaxLimit = ({
  containerSize,
  elementSize,
  scale,
}: MaxLimitParams): number => {
  const bufferPercent =
    (EDGE_BUFFER_PX / (elementSize * scale)) * PERCENTAGE_SCALE;
  return (
    (((UNSCALED_RATIO + scale) / CENTER_DIVISOR - containerSize / elementSize) *
      PERCENTAGE_SCALE) /
      scale -
    bufferPercent
  );
};

export const constrainToBounds = (
  value: number,
  min: number,
  max: number,
): number => Math.min(max, Math.max(min, value));

export const calculateZoomScaleLevels = ({
  containerSize,
  contentSize,
  zoomScaleLevels,
  devicePixelRatio,
}: {
  containerSize: number;
  contentSize: number;
  zoomScaleLevels: number[];
  devicePixelRatio: number;
}): number[] => {
  const unzoomedScale = containerSize / contentSize;

  if (!Number.isFinite(unzoomedScale) || unzoomedScale <= 0) {
    return [];
  }

  const pixelRatioScale =
    UNSCALED_RATIO / normaliseDevicePixelRatio(devicePixelRatio);
  const maxScale =
    pixelRatioScale > unzoomedScale ? pixelRatioScale : UNSCALED_RATIO;

  return zoomScaleLevels.map(
    (zoomScaleLevel) =>
      unzoomedScale * (maxScale / unzoomedScale) ** zoomScaleLevel,
  );
};

export const calculateConstrainedPan = ({
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
  scale,
  currentX,
  currentY,
  deltaX,
  deltaY,
}: ConstrainedPanParams): { x: number; y: number } => {
  const minOffsetX = calculateMinLimit({ elementSize: contentWidth, scale });
  const maxOffsetX = calculateMaxLimit({
    containerSize: containerWidth,
    elementSize: contentWidth,
    scale,
  });
  const minOffsetY = calculateMinLimit({ elementSize: contentHeight, scale });
  const maxOffsetY = calculateMaxLimit({
    containerSize: containerHeight,
    elementSize: contentHeight,
    scale,
  });

  return {
    x: constrainToBounds(currentX + deltaX, minOffsetX, maxOffsetX),
    y: constrainToBounds(currentY + deltaY, minOffsetY, maxOffsetY),
  };
};

export const getHorizontalPanEdge = ({
  containerWidth,
  contentWidth,
  scale,
  positionX,
}: {
  containerWidth: number;
  contentWidth: number;
  scale: number;
  positionX: number;
}): "left" | "right" | null => {
  const minOffsetX = calculateMinLimit({ elementSize: contentWidth, scale });
  const maxOffsetX = calculateMaxLimit({
    containerSize: containerWidth,
    elementSize: contentWidth,
    scale,
  });

  if (positionX <= minOffsetX) {
    return "left";
  }

  if (positionX >= maxOffsetX) {
    return "right";
  }

  return null;
};

export const calculateContentFocalPoint = ({
  rawFocalX,
  rawFocalY,
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
  scale,
  positionX,
  positionY,
}: ContentFocalPointParams): { focalX: number; focalY: number } => {
  const visibleFractionX = containerWidth / (scale * contentWidth);
  const visibleFractionY = containerHeight / (scale * contentHeight);

  return {
    focalX:
      rawFocalX * visibleFractionX +
      positionX / PERCENTAGE_SCALE +
      CONTENT_CENTER -
      1 / (CENTER_DIVISOR * scale),
    focalY:
      rawFocalY * visibleFractionY +
      positionY / PERCENTAGE_SCALE +
      CONTENT_CENTER -
      1 / (CENTER_DIVISOR * scale),
  };
};
